import { FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useError } from '@/hooks/useError';
import styles from './Contact.module.scss';

type FormState = 'idle' | 'submitting' | 'success';

type TouchedFields = Record<'name' | 'email' | 'message', boolean>;

const EMAIL = import.meta.env.VITE_CONTACT_EMAIL;
const LINK_ICON_SIZE = 18;
const SUCCESS_ICON_SIZE = 32;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SOCIAL_LINKS = [
  { href: import.meta.env.VITE_LINKEDIN_URL, icon: 'linkedin' },
  { href: import.meta.env.VITE_INSTAGRAM_URL, icon: 'instagram' },
  { href: import.meta.env.VITE_GITLAB_URL, icon: 'gitlab' }
] as const;

const inputCls = (base: string, hasError: boolean) => `${base}${hasError ? ` ${styles.inputError}` : ''}`;

const Contact = () => {
  const { t } = useTranslation();
  const { pushError } = useError();

  const nameId = useId();
  const emailId = useId();
  const messageId = useId();
  const nameErrorId = `${nameId}-error`;
  const emailErrorId = `${emailId}-error`;
  const messageErrorId = `${messageId}-error`;

  const [formState, setFormState] = useState<FormState>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState<TouchedFields>({ name: false, email: false, message: false });

  const touch = (field: keyof TouchedFields) => setTouched((prev) => ({ ...prev, [field]: true }));

  const errors = {
    name: name.trim() === '' ? t('contact.form.nameRequired') : null,
    email:
      email === '' ? t('contact.form.emailRequired') : !EMAIL_REGEX.test(email) ? t('contact.form.emailInvalid') : null,
    message: message.trim() === '' ? t('contact.form.messageRequired') : null
  };

  const isValid = !errors.name && !errors.email && !errors.message;
  const isSubmitting = formState === 'submitting';

  const visibleErrors = {
    name: touched.name ? errors.name : null,
    email: touched.email ? errors.email : null,
    message: touched.message ? errors.message : null
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) {
      setTouched({ name: true, email: true, message: true });
      return;
    }
    setFormState('submitting');

    const { error } = await supabase.functions.invoke('send-contact-email', {
      body: { name, email, message }
    });

    if (error) {
      pushError(t('error.contactSendFailed'));
      setFormState('idle');
      return;
    }

    setFormState('success');
  };

  return (
    <div className={styles.contact}>
      <div className={styles.formArea}>
        {formState === 'success' ? (
          <div className={styles.success}>
            <svg width={SUCCESS_ICON_SIZE} height={SUCCESS_ICON_SIZE} aria-hidden='true' className={styles.successIcon}>
              <use href='/icons.svg#check-circle' />
            </svg>
            <p className={styles.successTitle}>{t('contact.form.successTitle')}</p>
            <p className={styles.successBody}>{t('contact.form.successBody')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label htmlFor={nameId} className={styles.label}>
                  {t('contact.form.name')}
                </label>
                <input
                  id={nameId}
                  type='text'
                  className={inputCls(styles.input, !!visibleErrors.name)}
                  placeholder={t('contact.form.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => touch('name')}
                  disabled={isSubmitting}
                  aria-invalid={!!visibleErrors.name}
                  aria-describedby={visibleErrors.name ? nameErrorId : undefined}
                />
                {visibleErrors.name && (
                  <p id={nameErrorId} className={styles.fieldError} role='alert'>
                    {visibleErrors.name}
                  </p>
                )}
              </div>
              <div className={styles.field}>
                <label htmlFor={emailId} className={styles.label}>
                  {t('contact.form.email')}
                </label>
                <input
                  id={emailId}
                  type='email'
                  className={inputCls(styles.input, !!visibleErrors.email)}
                  placeholder={t('contact.form.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => touch('email')}
                  disabled={isSubmitting}
                  aria-invalid={!!visibleErrors.email}
                  aria-describedby={visibleErrors.email ? emailErrorId : undefined}
                />
                {visibleErrors.email && (
                  <p id={emailErrorId} className={styles.fieldError} role='alert'>
                    {visibleErrors.email}
                  </p>
                )}
              </div>
            </div>
            <div className={styles.field}>
              <label htmlFor={messageId} className={styles.label}>
                {t('contact.form.message')}
              </label>
              <textarea
                id={messageId}
                className={inputCls(styles.textarea, !!visibleErrors.message)}
                placeholder={t('contact.form.messagePlaceholder')}
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onBlur={() => touch('message')}
                disabled={isSubmitting}
                aria-invalid={!!visibleErrors.message}
                aria-describedby={visibleErrors.message ? messageErrorId : undefined}
              />
              {visibleErrors.message && (
                <p id={messageErrorId} className={styles.fieldError} role='alert'>
                  {visibleErrors.message}
                </p>
              )}
            </div>
            <button type='submit' className={styles.submitBtn} disabled={!isValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <svg
                    className={styles.spinner}
                    width='16'
                    height='16'
                    viewBox='0 0 24 24'
                    fill='none'
                    aria-hidden='true'
                  >
                    <circle
                      cx='12'
                      cy='12'
                      r='9'
                      stroke='currentColor'
                      strokeWidth='2.5'
                      strokeLinecap='round'
                      strokeDasharray='38 20'
                    />
                  </svg>
                  {t('contact.form.submitting')}
                </>
              ) : (
                t('contact.form.submit')
              )}
            </button>
          </form>
        )}
      </div>

      <div className={styles.directContact}>
        <div className={styles.divider}>
          <span>{t('contact.reachDirectly')}</span>
        </div>

        <div className={styles.links}>
          <a href={`mailto:${EMAIL}`} className={styles.linkItem} aria-label={EMAIL}>
            <svg width={LINK_ICON_SIZE} height={LINK_ICON_SIZE} aria-hidden='true'>
              <use href='/icons.svg#mail' />
            </svg>
          </a>

          {SOCIAL_LINKS.map(({ href, icon }) => (
            <a key={icon} href={href} className={styles.linkItem} target='_blank' rel='noopener noreferrer'>
              <svg width={LINK_ICON_SIZE} height={LINK_ICON_SIZE} aria-hidden='true'>
                <use href={`/icons.svg#${icon}`} />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Contact;
